import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { whyVoxmasterStats, differentiators, comparisonRows, businessTypes } from "@/mocks/why-voxmaster";

const diffColorMap: Record<string, { iconBg: string; ring: string }> = {
  primary: { iconBg: "bg-primary-100 text-primary-700", ring: "ring-primary-400" },
  accent: { iconBg: "bg-accent-100 text-accent-700", ring: "ring-accent-400" },
  secondary: { iconBg: "bg-secondary-100 text-secondary-700", ring: "ring-secondary-400" },
};

const bizColorMap: Record<string, string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-700",
  secondary: "bg-secondary-100 text-secondary-700",
};

export default function WhyVoxmaster() {
  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-14 overflow-hidden relative">
          <div className="absolute inset-0 vx-grid-bg pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-primary-100/60 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-[300px] h-[300px] rounded-full bg-accent-100/50 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-5">
              <Link to="/" className="hover:text-primary-600 cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center" />
              <span className="text-foreground-800">Why Voxmaster</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-semibold text-foreground-950 leading-tight tracking-tight max-w-4xl">
              Why carriers choose Voxmaster.
            </h1>
            <p className="mt-4 text-base md:text-lg text-foreground-700 max-w-3xl leading-relaxed">
              Stability, route quality, transparency, and a human NOC that actually picks up. Six reasons operators switch — and stay.
            </p>
          </div>
        </section>

        {/* Stats row */}
        <section className="pb-10 md:pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-0 border border-background-200 rounded-xl overflow-hidden bg-background-50">
              {whyVoxmasterStats.map((s) => (
                <div key={s.label} className="p-5 border-b border-r border-background-200 last:border-r-0 text-center">
                  <p className="text-2xl md:text-3xl font-semibold text-foreground-950 font-heading">{s.value}</p>
                  <p className="text-xs text-foreground-600 mt-1 leading-tight">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Differentiators */}
        <section className="py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">The difference</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight">
                Six reasons operators choose Voxmaster.
              </h2>
              <p className="mt-3 text-foreground-700 max-w-3xl leading-relaxed">
                Every carrier promises quality. We measure it, publish it, and auto-correct it before you notice. Here is what that actually means in practice.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {differentiators.map((d) => {
                const dc = diffColorMap[d.color];
                return (
                  <div key={d.title} className={`rounded-xl border border-background-200 bg-background-50 p-6 hover:ring-1 ${dc.ring} transition-all`}>
                    <span className={`w-12 h-12 rounded-lg ${dc.iconBg} flex items-center justify-center`}>
                      <i className={`${d.icon} text-2xl w-7 h-7 flex items-center justify-center`} />
                    </span>
                    <h3 className="mt-4 text-lg font-semibold text-foreground-950 font-heading">{d.title}</h3>
                    <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{d.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Businesses we serve */}
        <section className="py-14 md:py-20 bg-background-100/60 border-y border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-primary-600 font-semibold">Who we serve</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-foreground-950 leading-tight max-w-3xl">
                We provide communications solutions for the following businesses.
              </h2>
              <p className="mt-3 text-foreground-700 max-w-3xl leading-relaxed">
                In the modern world, if you want to stay up to date you need to communicate with your clients in multiple ways. That's why we provide the best enterprise communications services on the market.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {businessTypes.map((b) => (
                <div key={b.title} className="rounded-xl border border-background-200 bg-background-50 p-6 hover:border-background-300 transition-colors">
                  <span className={`w-12 h-12 rounded-lg ${bizColorMap[b.color]} flex items-center justify-center`}>
                    <i className={`${b.icon} text-2xl w-7 h-7 flex items-center justify-center`} />
                  </span>
                  <h3 className="mt-4 text-base font-semibold text-foreground-950 font-heading">{b.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison table */}
        <section className="py-14 md:py-20 bg-secondary-950 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-secondary-950 via-secondary-900 to-secondary-950 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-10">
              <span className="text-xs uppercase tracking-widest text-accent-400 font-semibold">Voxmaster vs. typical carriers</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-semibold text-background-50 leading-tight">
                The numbers do not lie.
              </h2>
              <p className="mt-3 text-background-300 max-w-3xl leading-relaxed">
                We benchmarked our operational performance against typical wholesale carrier practices. Here is how we compare across the metrics that actually matter for your traffic.
              </p>
            </div>

            <div className="rounded-xl border border-secondary-800 overflow-hidden bg-secondary-900/60">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-secondary-800 text-left text-xs uppercase tracking-wider text-background-300">
                    <th className="px-5 py-4 font-medium">Operational metric</th>
                    <th className="px-5 py-4 font-medium">Voxmaster</th>
                    <th className="px-5 py-4 font-medium">Typical carrier</th>
                    <th className="px-5 py-4 font-medium">Advantage</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((r) => (
                    <tr key={r.feature} className="border-b border-secondary-800 last:border-0 hover:bg-secondary-800/30 transition-colors">
                      <td className="px-5 py-4 font-medium text-background-100">{r.feature}</td>
                      <td className="px-5 py-4 text-accent-400 font-semibold">{r.voxmaster}</td>
                      <td className="px-5 py-4 text-background-300">{r.typical}</td>
                      <td className="px-5 py-4">
                        <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${
                          r.winner === "voxmaster" ? "bg-accent-100/20 text-accent-400" : "bg-secondary-700/50 text-background-300"
                        }`}>
                          {r.winner === "voxmaster" ? "Voxmaster" : "Tie"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-14 md:py-20 bg-primary-500 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/40 to-transparent pointer-events-none" />
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-background-50/10 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-semibold text-background-50 font-heading leading-tight">
              Experience the difference yourself.
            </h2>
            <p className="mt-4 text-background-100 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Set up a test trunk, run live quality probes against your top destinations, and see the ASR and PDD numbers in real time. No obligation, no hard sell.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-background-50 text-primary-700 px-6 py-3.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-100 transition-colors"
              >
                Start a test
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
              <Link
                to="/network"
                className="inline-flex items-center gap-2 bg-background-50/15 text-background-50 border border-background-50/30 px-6 py-3.5 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-background-50/25 transition-colors"
              >
                View network details
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}